import React, { useEffect, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, FormControl, FormLabel, FormHelperText, Input,
  Select, Switch, Textarea, SimpleGrid, Divider, Text, useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import ratePlanService from '../../services/ratePlanService';
import { RATE_TYPES, RATE_UNITS, apiError } from './format';

const EMPTY = {
  name: '', code: '', rate_type: 'standard', description: '',
  is_refundable: true, requires_advance_payment: false, advance_purchase_days: '',
  cancellation_hours: 24, cancellation_penalty_percent: '0.00',
  is_active: true, is_public: true, is_base_plan: false, display_order: 1,
  rate_unit: 'night', fallback_rate_plan: '',
};

const toForm = (plan) => (plan ? {
  name: plan.name || '',
  code: plan.code || '',
  rate_type: plan.rate_type || 'standard',
  description: plan.description || '',
  is_refundable: plan.is_refundable ?? true,
  requires_advance_payment: plan.requires_advance_payment ?? false,
  advance_purchase_days: plan.advance_purchase_days ?? '',
  cancellation_hours: plan.cancellation_hours ?? 24,
  cancellation_penalty_percent: plan.cancellation_penalty_percent ?? '0.00',
  is_active: plan.is_active ?? true,
  is_public: plan.is_public ?? true,
  is_base_plan: plan.is_base_plan ?? false,
  display_order: plan.display_order ?? 1,
  rate_unit: plan.rate_unit || 'night',
  fallback_rate_plan: plan.fallback_rate_plan || '',
} : EMPTY);

// format.js owns these option lists; translate a key if it carries one.
const optionLabel = (t, item) => (item.labelKey ? t(item.labelKey) : item.label);

const SWITCHES = [
  ['is_active', 'ratePlanSettings.switches.active'],
  ['is_public', 'ratePlanSettings.switches.shownToGuests'],
  ['is_base_plan', 'ratePlanSettings.switches.basePlan'],
  ['is_refundable', 'ratePlanSettings.switches.refundable'],
  ['requires_advance_payment', 'ratePlanSettings.switches.payAtBooking'],
];

const PlanSettingsModal = ({ isOpen, onClose, plan, plans, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const { t } = useTranslation();

  useEffect(() => { if (isOpen) setForm(toForm(plan)); }, [isOpen, plan]);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const toggle = (field) => (e) => setForm({ ...form, [field]: e.target.checked });

  // Only per-night plans can price a weekly/monthly plan's leftover nights.
  const fallbackOptions = plans.filter(
    (p) => p.rate_unit === 'night' && (!plan || p.id !== plan.id));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      payload.advance_purchase_days = form.advance_purchase_days === ''
        ? null : Number(form.advance_purchase_days);
      payload.cancellation_hours = Number(form.cancellation_hours || 0);
      payload.display_order = Number(form.display_order || 1);
      payload.fallback_rate_plan = form.rate_unit !== 'night' && form.fallback_rate_plan
        ? Number(form.fallback_rate_plan) : null;

      const saved = plan
        ? await ratePlanService.updatePlan(plan.id, payload)
        : await ratePlanService.createPlan(payload);
      toast({ title: plan ? t('ratePlanSettings.toasts.saved') : t('ratePlanSettings.toasts.created'), status: 'success', duration: 2000 });
      onSaved(saved);
      onClose();
    } catch (err) {
      toast({ title: t('ratePlanSettings.toasts.saveFailed'), description: apiError(err), status: 'error', duration: 6000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{plan ? t('ratePlanSettings.modal.editTitle', { name: plan.name }) : t('ratePlanSettings.modal.newTitle')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            <FormControl isRequired gridColumn={{ md: 'span 2' }}>
              <FormLabel fontSize="sm">{t('ratePlanSettings.labels.name')}</FormLabel>
              <Input size="sm" value={form.name} onChange={set('name')} placeholder={t('ratePlanSettings.labels.namePlaceholder')} />
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontSize="sm">{t('ratePlanSettings.labels.code')}</FormLabel>
              <Input size="sm" value={form.code} onChange={set('code')} placeholder="BAR" />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">{t('ratePlanSettings.labels.type')}</FormLabel>
              <Select size="sm" value={form.rate_type} onChange={set('rate_type')}>
                {RATE_TYPES.map((x) => <option key={x.value} value={x.value}>{optionLabel(t, x)}</option>)}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">{t('ratePlanSettings.labels.pricesAre')}</FormLabel>
              <Select size="sm" value={form.rate_unit} onChange={set('rate_unit')}>
                {RATE_UNITS.map((u) => <option key={u.value} value={u.value}>{optionLabel(t, u)}</option>)}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">{t('ratePlanSettings.labels.displayOrder')}</FormLabel>
              <Input size="sm" type="number" min={1} value={form.display_order} onChange={set('display_order')} />
            </FormControl>

            {form.rate_unit !== 'night' && (
              <FormControl gridColumn={{ md: 'span 3' }}>
                <FormLabel fontSize="sm">{t('ratePlanSettings.labels.fallbackPlan')}</FormLabel>
                <Select size="sm" value={form.fallback_rate_plan} onChange={set('fallback_rate_plan')}
                  placeholder={t('ratePlanSettings.labels.fallbackPlaceholder')}>
                  {fallbackOptions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </Select>
                <FormHelperText fontSize="xs">
                  {t('ratePlanSettings.labels.fallbackHelp')}
                </FormHelperText>
              </FormControl>
            )}

            <FormControl gridColumn={{ md: 'span 3' }}>
              <FormLabel fontSize="sm">{t('ratePlanSettings.labels.description')}</FormLabel>
              <Textarea size="sm" rows={2} value={form.description} onChange={set('description')} />
            </FormControl>
          </SimpleGrid>

          <Divider my={4} />
          <Text fontSize="sm" fontWeight="600" mb={3}>{t('ratePlanSettings.labels.bookingConditions')}</Text>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            <FormControl>
              <FormLabel fontSize="sm">{t('ratePlanSettings.labels.freeCancellationUntil')}</FormLabel>
              <Input size="sm" type="number" min={0} value={form.cancellation_hours} onChange={set('cancellation_hours')} />
              <FormHelperText fontSize="xs">{t('ratePlanSettings.labels.hoursBeforeArrival')}</FormHelperText>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">{t('ratePlanSettings.labels.latePenalty')}</FormLabel>
              <Input size="sm" type="number" min={0} max={100} step="0.01"
                value={form.cancellation_penalty_percent} onChange={set('cancellation_penalty_percent')} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">{t('ratePlanSettings.labels.advancePurchaseDays')}</FormLabel>
              <Input size="sm" type="number" min={0} value={form.advance_purchase_days}
                onChange={set('advance_purchase_days')} placeholder={t('ratePlanSettings.labels.noMinimum')} />
            </FormControl>
          </SimpleGrid>

          <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3} mt={4}>
            {SWITCHES.map(([field, labelKey]) => (
              <FormControl key={field} display="flex" alignItems="center">
                <Switch size="sm" mr={2} isChecked={form[field]} onChange={toggle(field)} />
                <FormLabel fontSize="sm" mb={0}>{t(labelKey)}</FormLabel>
              </FormControl>
            ))}
          </SimpleGrid>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>{t('ratePlanSettings.buttons.cancel')}</Button>
          <Button colorScheme="blue" onClick={save} isLoading={saving}
            isDisabled={!form.name.trim() || !form.code.trim()}>
            {plan ? t('ratePlanSettings.buttons.save') : t('ratePlanSettings.buttons.create')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PlanSettingsModal;
